"use client";

import { Check, Copy, IdCard, Link2, Radio } from "lucide-react";
import { useActionState, useState } from "react";
import { inputClass } from "@/components/forms/Field";
import type { ClaimsText } from "@/lib/claims/i18n";
import { checkRoomAction, type ClaimState, submitIdAction, submitLinkAction } from "@/lib/claims/actions";
import { fmt } from "@/lib/locales/fmt";

// The claim's code and the ways to show it (app/[lang]/(site)/claim): in the room's title (checked at once), on an
// account of the claimant's, or with their ID. One is enough.

const button =
  "inline-flex h-11 items-center justify-center gap-2 rounded-full bg-brand-strong px-5 text-[14.5px] font-semibold text-white transition hover:bg-brand-deep disabled:opacity-60";

function Result({ state }: { state: ClaimState }) {
  if (state.error)
    return (
      <p role="alert" className="mt-3 rounded-xl border border-brand/30 bg-brand/[0.08] px-3.5 py-2.5 text-[13.5px] text-fg">
        {state.error}
      </p>
    );
  if (state.message)
    return (
      <p role="status" className="mt-3 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-3.5 py-2.5 text-[13.5px] text-emerald-200">
        {state.message}
      </p>
    );
  return null;
}

function Way({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-line bg-raised p-5">
      <h3 className="flex items-center gap-2 text-[17px] text-fg">
        <span className="grid size-8 place-items-center rounded-full bg-brand/15 text-brand-soft">{icon}</span>
        {title}
      </h3>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export function ClaimCode({ code, t }: { code: string; t: ClaimsText }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-2xl border border-brand/30 bg-brand/[0.07] p-5">
      <p className="text-[12px] uppercase tracking-[0.1em] text-faint">{t.yourCode}</p>
      <div className="mt-1 flex flex-wrap items-center gap-3">
        <code className="select-all font-mono text-[28px] font-semibold tracking-[0.08em] text-fg">{code}</code>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(code).then(() => setCopied(true));
          }}
          className="chip inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] text-soft hover:text-fg"
        >
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
          {copied ? t.copied : t.copy}
        </button>
      </div>
      <p className="mt-2 text-[13px] text-muted">{t.codeHint}</p>
    </div>
  );
}

export function ClaimWays({ claimId, code, lang, t, room, canId }: { claimId: string; code: string; lang: string; t: ClaimsText; room: { platform: string; titled: boolean } | null; canId: boolean }) {
  const [roomState, roomAction, checking] = useActionState<ClaimState, FormData>(checkRoomAction, {});
  const [linkState, linkAction, sendingLink] = useActionState<ClaimState, FormData>(submitLinkAction, {});
  const [idState, idAction, sendingId] = useActionState<ClaimState, FormData>(submitIdAction, {});
  const hidden = (
    <>
      <input type="hidden" name="claim" value={claimId} />
      <input type="hidden" name="ui" value={lang} />
    </>
  );
  const strongCode = (text: string, vars: Record<string, string> = {}) => {
    const [before, after] = fmt(text, { ...vars, code: "\u0000" }).split("\u0000");
    return (
      <>
        {before}
        <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-[13px] text-fg">{code}</code>
        {after}
      </>
    );
  };

  return (
    <div className="space-y-4">
      <h2 className="text-[20px] text-fg">{t.waysTitle}</h2>
      {room && (
        <Way icon={<Radio size={16} aria-hidden />} title={t.roomTitle}>
          {room.titled ? (
            <form action={roomAction}>
              {hidden}
              <p className="text-[14px] leading-relaxed text-soft">{strongCode(t.roomText, { platform: room.platform })}</p>
              <button type="submit" disabled={checking} className={`${button} mt-4`}>
                {checking ? t.roomChecking : t.roomCheck}
              </button>
              <Result state={roomState} />
            </form>
          ) : (
            <p className="text-[14px] text-muted">{fmt(t.roomUnavailable, { platform: room.platform })}</p>
          )}
        </Way>
      )}

      <Way icon={<Link2 size={16} aria-hidden />} title={t.linkTitle}>
        <form action={linkAction} className="space-y-3">
          {hidden}
          <p className="text-[14px] leading-relaxed text-soft">{strongCode(t.linkText)}</p>
          <label className="block">
            <span className="mb-1.5 block text-[13px] text-muted">{t.linkLabel}</span>
            <input name="url" type="url" inputMode="url" required maxLength={300} placeholder="https://" className={`${inputClass} h-11`} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] text-muted">{t.messageLabel}</span>
            <textarea name="message" rows={2} maxLength={1000} className={inputClass} />
          </label>
          <button type="submit" disabled={sendingLink} className={button}>
            {sendingLink ? t.sending : t.send}
          </button>
          <Result state={linkState} />
        </form>
      </Way>

      <Way icon={<IdCard size={16} aria-hidden />} title={t.idTitle}>
        {canId ? (
          <form action={idAction} className="space-y-3">
            {hidden}
            <p className="text-[14px] leading-relaxed text-soft">{strongCode(t.idText)}</p>
            <label className="block">
              <span className="mb-1.5 block text-[13px] text-muted">{t.documentLabel}</span>
              <input name="document" type="file" accept="image/jpeg,image/png,image/webp" required className="block w-full text-[13.5px] text-soft file:mr-3 file:rounded-full file:border-0 file:bg-surface file:px-4 file:py-2 file:text-fg" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[13px] text-muted">{t.selfieLabel}</span>
              <input name="selfie" type="file" accept="image/jpeg,image/png,image/webp" required className="block w-full text-[13.5px] text-soft file:mr-3 file:rounded-full file:border-0 file:bg-surface file:px-4 file:py-2 file:text-fg" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[13px] text-muted">{t.idLinkLabel}</span>
              <input name="url" type="url" inputMode="url" maxLength={300} placeholder="https://" className={`${inputClass} h-11`} />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[13px] text-muted">{t.messageLabel}</span>
              <textarea name="message" rows={2} maxLength={1000} className={inputClass} />
            </label>
            <button type="submit" disabled={sendingId} className={button}>
              {sendingId ? t.sending : t.send}
            </button>
            <Result state={idState} />
          </form>
        ) : (
          <p className="text-[14px] text-muted">{t.idUnavailable}</p>
        )}
      </Way>
    </div>
  );
}
